<script setup lang="ts">
/**
 * "Export for Editing" panel — region checklist + resolution, brief
 * 03-procreate.md's Export Dialog.
 */
import { computed, ref } from 'vue';
import { IconChevronLeft } from '@tabler/icons-vue';
import { useLayersStore } from '@/stores/layers';
import { exportPsd } from '@/services/psd/psdExport';

const emit = defineEmits<{ back: []; done: [] }>();

const layers = useLayersStore();

const RESOLUTIONS = [1024, 2048, 4096] as const;
const resolution = ref<number>(2048);

const selected = ref<Set<string>>(new Set(layers.traceLayers.map((l) => l.id)));

function toggle(id: string): void {
  const next = new Set(selected.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selected.value = next;
}

// With no traced regions at all there's nothing to check off, but export
// should still proceed with just the reference/guide layers (buildExportPsd
// already handles opts.regionIds=[] correctly) — matching the empty-state
// message above, which promises exactly that. Only require a selection when
// there's actually something selectable.
const canExport = computed(() => selected.value.size > 0 || layers.traceLayers.length === 0);

function doExport(): void {
  exportPsd({ regionIds: [...selected.value], resolution: resolution.value });
  emit('done');
}
</script>

<template>
  <div class="panel">
    <button class="back-link" @click="emit('back')">
      <IconChevronLeft :size="15" stroke-width="2" />
      Back
    </button>

    <p v-if="layers.traceLayers.length === 0" class="empty">
      No traced vector regions yet — export anyway and you'll get a whole-tile paint layer
      (one per colour when colour tiling is on), masked to the tile shape, plus the sketch/tile
      reference layers. Or use the Trace tool first to get a separate masked paint layer per
      shape (e.g. "Flowers", "Leaves").
    </p>

    <ul v-else class="region-list">
      <li v-for="region in layers.traceLayers" :key="region.id">
        <label class="region-row">
          <input type="checkbox" :checked="selected.has(region.id)" @change="toggle(region.id)" />
          <span>{{ region.name }}</span>
        </label>
      </li>
    </ul>

    <div class="resolution-row">
      <span class="label">Resolution</span>
      <div class="seg">
        <button
          v-for="r in RESOLUTIONS"
          :key="r"
          class="seg-btn"
          :class="{ active: resolution === r }"
          @click="resolution = r"
        >
          {{ r }}×{{ r }}
        </button>
      </div>
    </div>

    <button class="primary-btn" :disabled="!canExport" @click="doExport">Export PSD</button>
  </div>
</template>

<style scoped>
.panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  background: transparent;
  border: none;
  color: var(--text-muted);
  font-size: 12.5px;
  cursor: pointer;
  padding: 0;
}

.back-link:hover {
  color: var(--text-primary);
}

.empty {
  color: var(--text-muted);
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
}

.region-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.region-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: var(--canvas-bg);
  border: 1px solid var(--ui-border);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 13.5px;
  cursor: pointer;
}

.resolution-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.label {
  font-size: 12px;
  color: var(--text-muted);
}

.seg {
  display: flex;
  gap: 2px;
  background: color-mix(in srgb, var(--ui-border) 35%, transparent);
  border: 1px solid var(--ui-border);
  border-radius: 9px;
  padding: 2px;
}

.seg-btn {
  min-height: 30px;
  padding: 4px 10px;
  font-size: 12px;
  background: transparent;
  color: var(--text-primary);
  border: none;
  border-radius: 7px;
  cursor: pointer;
}

.seg-btn.active {
  background: color-mix(in srgb, var(--accent) 22%, transparent);
}

.primary-btn {
  min-height: 42px;
  background: var(--accent);
  color: var(--accent-contrast);
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.primary-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
